import { Fragment } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Eyebrow, LuxButton } from "../common/Primitives";
import { Reveal } from "../motion/Reveal";
import { IMG } from "../../config/site";
import { useUI } from "../../context/UIContext";

export const STEPS = [
  { title: "Water Source", img: IMG.source, alt: "Clear water flowing over rocks", text: "Source selection and water testing are essential starting points for a quality-focused bottling process." },
  { title: "Treatment", img: IMG.treatment, alt: "Stainless-steel treatment tanks", text: "Water treatment is selected according to source-water analysis and applicable requirements." },
  { title: "Mineral Balancing", img: IMG.lab, alt: "A gloved hand holding a beaker of water in a laboratory", text: "Any mineral adjustment must follow the approved formulation and applicable food-safety requirements." },
  { title: "Bottling", img: IMG.bottling, alt: "Clear glass bottles on a hygienic filling line", text: "Bottles are prepared and filled using an appropriate, hygienic bottling process." },
  { title: "Quality Checks", img: IMG.inspection, alt: "A technician inspecting a glass bottle against a light panel", text: "Testing, batch traceability, packaging inspection, and documented quality controls support product consistency." },
];

const Step = ({ s, i }) => (
  <motion.article className="group flex-1" data-testid={`process-step-${i + 1}`}
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
    <div className="aspect-[4/3] overflow-hidden">
      <img src={s.img} alt={s.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.6s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.07]" />
    </div>
    <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.28em] text-[#B18A4A]">Step {i + 1}</p>
    <h3 className="mt-1 font-sans text-[12px] font-bold uppercase tracking-[0.22em] text-[#26231F]">{s.title}</h3>
    <p className="mt-2 text-[13px] leading-relaxed text-[#746D64]">{s.text}</p>
  </motion.article>
);

export const Process = () => {
  const { openPanel } = useUI();
  return (
    <section id="process" data-testid="process-section" className="bg-[#FBF8F2] py-24 sm:py-32">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal className="grid items-end gap-8 lg:grid-cols-[1.1fr_1.4fr_auto]">
          <div>
            <Eyebrow>Our Process</Eyebrow>
            <h2 className="h-section mt-6">From Source to Service</h2>
          </div>
          <p className="body-lux max-w-lg">A carefully managed process, designed around the quality and presentation expectations of hospitality partners.</p>
          <LuxButton variant="line-dark" arrow onClick={() => openPanel("process")} data-testid="process-learn-more-btn">Learn more</LuxButton>
        </Reveal>
        <div className="mt-16 flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-3">
          {STEPS.map((s, i) => (
            <Fragment key={s.title}>
              <Step s={s} i={i} />
              {i < STEPS.length - 1 && (
                <span className="flex justify-center py-2 text-[#B18A4A] lg:mt-[9%] lg:py-0" aria-hidden="true">
                  <ArrowRight className="hidden h-4 w-4 lg:block" strokeWidth={1.2} />
                  <ArrowDown className="h-4 w-4 lg:hidden" strokeWidth={1.2} />
                </span>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};
